import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@mui/material';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { DeviationsService } from '../../../../../Component/Api/EmploymentEquity/Deviations/DeviationsService';
import { ConfirmationDialog } from '../../../../../Component/Common/Components/ConfirmationDialog';
import { DataTable } from '../../../../../Component/Common/Components/DataTable/DataTable';
import { FilterBar, FilterValues } from '../../../../../Component/Common/Components/FilterBar/FilterBar';
import { IFilter, IFilterValue, FilterType } from '../../../../../Component/Common/Components/DataTable/Filters';
import { MDBox } from '../../../../../Component/Common/Components/MDBox';
import { MDButton } from '../../../../../Component/Common/Components/MDButton';
import { MDTypography } from '../../../../../Component/Common/Components/MDTypography';
import { Deviations } from '../../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { SearchResult } from '../../../../../Component/Model/Dto/SearchResult';
import { ColumnSortOption } from '../../../../../Component/Model/Dto/ColumnSortOption';
import { ScorecardAtom } from '../../../../../State/Scorecard.Atom';
import { DemographicGroup } from '../../../../../Component/Model/Enum/EmploymentEquity';
import { PortalDocuments } from '../../../../Common/PortalDocuments/PortalDocuments';
import colors from '../../../../../assets/theme/base/colors';
import { DemographicDeviationModal } from './DemographicDeviationModal';
import { DemographicJustificationsTableMapper } from './DemographicJustificationsTableColumn';

interface IProps {
    occupationalLevelType: number;
    isDisabled: boolean;
    isOpen: boolean;
    onJustificationsChanged?: () => void;
}

const filterDefinitions: IFilter[] = [
    {
        label: 'Demographic Group',
        propname: 'demographicGroups',
        type: FilterType.multiselect,
        values: Object.values(DemographicGroup).map(v => ({ key: v, val: v })),
    },
];

export const DemographicJustificationsTable = (props: IProps) => {
    const scorecard = useRecoilValue(ScorecardAtom);
    const scorecardId = scorecard.scorecardId;
    const [data, setData] = useState<SearchResult<Deviations.SearchResultDto> | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [filterBarValues, setFilterBarValues] = useState<FilterValues>({});
    const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [showDocumentsModal, setShowDocumentsModal] = useState<boolean>(false);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const request: Deviations.SearchRequest = {
                pageNumber: 1,
                pageSize: 100,
                searchValue: '',
                columnSortOptions,
                scorecardId,
                occupationalLevelType: props.occupationalLevelType,
                isDisabled: props.isDisabled,
                justificationType: 2,
            };
            const response = await DeviationsService.SearchJustifications(request);
            setData(response.data);
        } catch {
            toast.error('Failed to load demographic justifications');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!props.isOpen) return;
        fetchData();
    }, [columnSortOptions, props.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
    };

    const handleFilterChange = (filterValues: FilterValues) => {
        setFilterBarValues(filterValues);
    };

    const handleAdd = () => {
        setSelectedId(undefined);
        setShowModal(true);
    };

    const handleEdit = (id: number) => {
        setSelectedId(id);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedId(undefined);
        fetchData();
        props.onJustificationsChanged?.();
    };

    const handleConfirmDelete = (id: number) => {
        setSelectedId(id);
        setShowDeleteDialog(true);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await DeviationsService.Delete(scorecardId, selectedId!);
            toast.success('Deviation deleted successfully');
            fetchData();
            props.onJustificationsChanged?.();
        } catch {
            toast.error('Failed to delete deviation');
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
            setSelectedId(undefined);
        }
    };

    const handleCloseDeleteDialog = () => {
        setShowDeleteDialog(false);
        setSelectedId(undefined);
    };

    const handleDocuments = (id: number) => {
        setSelectedId(id);
        setShowDocumentsModal(true);
    };

    const handleCloseDocuments = () => {
        setShowDocumentsModal(false);
        setSelectedId(undefined);
        fetchData();
        props.onJustificationsChanged?.();
    };

    const filteredResults = useMemo(() => {
        const results = data?.results || [];
        const selectedGroups = (filterBarValues.demographicGroups as IFilterValue[])?.map(o => o.val as string) ?? [];
        if (selectedGroups.length === 0) return results;
        return results.filter(item => {
            const group = [item.race, item.gender].filter(Boolean).join(' ');
            return selectedGroups.includes(group);
        });
    }, [data, filterBarValues]);

    const tableData = DemographicJustificationsTableMapper(filteredResults, handleEdit, handleConfirmDelete, handleDocuments);

    return (
        <>
            <Card sx={{
                borderRadius: '10px',
                boxShadow: 'none',
                border: `0.8px solid ${colors.ui.border}`,
                '& .MuiPaper-root': {
                    boxShadow: 'none',
                    border: `0.8px solid ${colors.ui.border}`,
                },
                '& .MuiButton-outlinedPrimary': {
                    borderColor: colors.ui.activeBackground,
                    color: colors.ui.activeBackground,
                    borderWidth: '0.8px',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    padding: '6px 12px',
                    '&:hover': {
                        borderColor: colors.ui.activeBackgroundHover,
                        color: colors.ui.activeBackgroundHover,
                        backgroundColor: 'transparent',
                    },
                },
            }}>
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                    <MDBox mb={0.5}>
                        <MDTypography variant="h6" fontWeight="medium" textTransform="uppercase" color="dark">
                            Demographic justifications
                        </MDTypography>
                    </MDBox>
                    <MDBox mb={2}>
                        <MDTypography variant="caption" color="secondary" style={{ lineHeight: 1.4, display: 'block' }}>
                            Capture justification reasons at a specific demographic group level. Multiple justifications can be added per demographic group.
                        </MDTypography>
                    </MDBox>
                    <MDButton variant='outlined' color='primary' onClick={handleAdd} sx={{ mb: 2 }}>
                        ADD JUSTIFICATION
                    </MDButton>
                    <FilterBar filters={filterDefinitions} onChange={handleFilterChange} disableCard />
                    <DataTable
                        table={tableData}
                        isLoading={isLoading}
                        manualSorting={true}
                        columnSortingChanged={columnSortingChanged}
                        showTotalEntries={false}
                        showEntriesPerPage={false}
                        showBottomMargin={false}
                        noDataMessage="No justifications captured yet"
                    />
                </CardContent>
            </Card>

            {showModal && (
                <DemographicDeviationModal
                    showModal={showModal}
                    id={selectedId}
                    occupationalLevelType={props.occupationalLevelType}
                    scorecardId={scorecardId}
                    handleCancel={handleCloseModal}
                />
            )}
            {showDeleteDialog && (
                <ConfirmationDialog
                    showDialog={showDeleteDialog}
                    handleAccept={handleDelete}
                    handleCancel={handleCloseDeleteDialog}
                    heading='Delete Justification'
                    message='Are you sure you want to delete this justification and associated files?'
                    isLoading={isDeleting}
                />
            )}
            {showDocumentsModal && (
                <PortalDocuments
                    display={showDocumentsModal}
                    handleCancel={handleCloseDocuments}
                    link="deviation"
                    id={selectedId!}
                />
            )}
        </>
    );
};
